import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { LEGAL_UI } from "@/data/legalI18n";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { PasswordStrength } from "@/components/ui/password-strength";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { GoogleButton } from "@/components/auth/GoogleButton";
import { ACCOUNT_UI } from "@/data/accountI18n";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Moon, Loader2, MailCheck } from "lucide-react";
import { motion } from "framer-motion";
import { logWarn } from "@/lib/log";

const HERO = "https://static.prod-images.emergentagent.com/jobs/e3008f40-7468-48a5-b0f3-bace2e1b48d6/images/2d4ed0de2d19b722a89aacfa60e03126f6067cf620d5a9eaa0b5beddc4cbf5db.jpeg";

export default function AuthPage() {
  const { t, i18n } = useTranslation();
  const { tc } = useLang();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [panel, setPanel] = useState(""); // "" | verify | reset
  const [panelEmail, setPanelEmail] = useState("");
  const [resending, setResending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password, remember);
        navigate("/");
      } else if (mode === "register") {
        const res = await register(form.email, form.password, form.name);
        if (res?.message === "verification_sent") { setPanel("verify"); setPanelEmail(res.email); }
        else navigate("/");
      } else {
        await api.post("/auth/forgot-password", { email: form.email, lang: i18n.language });
        setPanel("reset"); setPanelEmail(form.email);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && detail === "email_not_verified") {
        setPanel("verify"); setPanelEmail(form.email);
      } else {
        setError(formatApiErrorDetail(detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try { await api.post("/auth/resend-verification", { email: panelEmail, lang: i18n.language }); }
    catch (e) { logWarn("resend verification failed:", e); }
    finally { setResending(false); toast.success(t("verifyEmailResent")); }
  };

  const backToLogin = () => {
    setPanel(""); setMode("login"); setError("");
    setForm({ ...form, password: "" });
  };

  const titleFor = { login: t("loginTitle"), register: t("registerTitle"), forgot: t("forgotPasswordTitle") }[mode];
  const subFor = mode === "forgot" ? t("forgotPasswordBody") : t("welcomeSub");

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={HERO} alt={t("tagline")} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1F2937]/80 via-[#1F2937]/20 to-transparent" />
        <div className="absolute bottom-0 p-12 text-white">
          <h2 className="font-serif text-4xl font-light mb-2">{t("appName")}</h2>
          <p className="text-white/80 max-w-sm">{t("tagline")}</p>
        </div>
      </div>

      <div className="flex flex-col geo-watermark">
        <div className="flex justify-end gap-2 p-4"><ThemeToggle /><LanguageSwitcher /></div>
        <div className="flex flex-1 items-center justify-center px-6 pb-16">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
            {panel ? (
              <div className="text-center" data-testid={`panel-${panel}`}>
                <span className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <MailCheck className="h-7 w-7" />
                </span>
                <h1 className="font-serif text-3xl">{t("verifyEmailSentTitle")}</h1>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  {panel === "verify" ? t("verifyEmailSentBody", { email: panelEmail }) : t("resetSentBody", { email: panelEmail })}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">{t("verifyEmailCheckSpam")}</p>
                {panel === "verify" && (
                  <Button onClick={resend} disabled={resending} variant="outline" data-testid="verify-resend-btn"
                    className="mt-6 h-12 w-full rounded-xl text-base font-semibold">
                    {resending ? <Loader2 className="h-5 w-5 animate-spin" /> : t("verifyEmailResend")}
                  </Button>
                )}
                <button onClick={backToLogin} data-testid="panel-back-login"
                  className="mt-4 w-full text-center text-sm text-muted-foreground hover:text-primary transition-colors">
                  {t("backToLogin")}
                </button>
              </div>
            ) : (
              <>
                <div className="mb-8 flex flex-col items-center text-center">
                  <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-white">
                    <Moon className="h-7 w-7" />
                  </span>
                  <h1 className="font-serif text-3xl">{titleFor}</h1>
                  <p className="text-sm text-muted-foreground mt-1">{subFor}</p>
                </div>

                <form onSubmit={submit} className="space-y-4">
                  {mode === "register" && (
                    <div className="space-y-1.5">
                      <Label htmlFor="name">{t("name")}</Label>
                      <Input id="name" data-testid="auth-name" required value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12 rounded-xl" />
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <Label htmlFor="email">{t("email")}</Label>
                    <Input id="email" type="email" data-testid="auth-email" required value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12 rounded-xl" />
                  </div>
                  {mode !== "forgot" && (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password">{t("password")}</Label>
                        {mode === "login" && (
                          <button type="button" onClick={() => { setMode("forgot"); setError(""); }}
                            data-testid="forgot-password-link"
                            className="text-xs text-muted-foreground hover:text-primary transition-colors">
                            {t("forgotPassword")}
                          </button>
                        )}
                      </div>
                      <PasswordInput id="password" data-testid="auth-password" required value={form.password}
                        autoComplete={mode === "login" ? "current-password" : "new-password"}
                        onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-12 rounded-xl" />
                      {mode === "register" && <PasswordStrength value={form.password} data-testid="register-password-strength" />}
                    </div>
                  )}
                  {mode === "login" && (
                    <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="remember-me-row">
                      <Checkbox checked={remember} onCheckedChange={(v) => setRemember(v === true)} data-testid="remember-me"
                        className="h-5 w-5 rounded-md" />
                      <span className="text-sm leading-tight">
                        {tc(ACCOUNT_UI.rememberMe)}
                        <span className="block text-xs text-muted-foreground">{tc(ACCOUNT_UI.rememberHint)}</span>
                      </span>
                    </label>
                  )}

                  {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}

                  <Button type="submit" disabled={loading} data-testid="auth-submit"
                    className="h-12 w-full rounded-xl text-base font-semibold">
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" />
                      : (mode === "login" ? t("login") : mode === "register" ? t("register") : t("sendResetLink"))}
                  </Button>
                </form>

                {mode !== "forgot" && (
                  <div className="mt-5 space-y-4" data-testid="social-login">
                    <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-muted-foreground">
                      <span className="h-px flex-1 bg-border" /><span>{tc(ACCOUNT_UI.orDivider)}</span><span className="h-px flex-1 bg-border" />
                    </div>
                    <GoogleButton />
                  </div>
                )}

                {mode === "forgot" ? (
                  <button onClick={backToLogin} data-testid="forgot-back-login"
                    className="mt-6 w-full text-center text-sm text-muted-foreground hover:text-primary transition-colors">
                    {t("backToLogin")}
                  </button>
                ) : (
                  <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
                    data-testid="auth-toggle-mode"
                    className="mt-6 w-full text-center text-sm text-muted-foreground hover:text-primary transition-colors">
                    {mode === "login" ? t("noAccount") : t("haveAccount")}{" "}
                    <span className="font-semibold text-primary">{mode === "login" ? t("register") : t("login")}</span>
                  </button>
                )}
                <div className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground" data-testid="auth-legal-links">
                  <Link to="/privacy" className="hover:text-primary" data-testid="auth-privacy-link">{tc(LEGAL_UI.privacyLink)}</Link>
                  <Link to="/install" className="hover:text-primary" data-testid="auth-install-link">{tc(LEGAL_UI.installLink)}</Link>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
